if ( typeof RecommendedProduct !== 'function' ) {

  class RecommendedProduct extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.init();
    }

    init() {
      // Drawer cart button (without form)
      const drawerButton = this.querySelector('[data-add-to-cart-drawer]');
      if (drawerButton) {
        this.initDrawerButton(drawerButton);
        return;
      }

      // Regular product form (for main cart page)
      const form = this.querySelector('#recommended-product-form');
      if (!form) return;

      const qtyInput = form.querySelector('.qty-selector');
      const qtyMinus = form.querySelector('.qty-minus');
      const qtyPlus = form.querySelector('.qty-plus');
      const priceContainer = this.querySelector('.recommended-product__actions-price');
      const priceElement = priceContainer?.querySelector('[data-line-price]');
      const compareAtPriceElement = priceContainer?.querySelector('[data-line-compare-at-price]');
      const compareAtPriceWrapper = priceContainer?.querySelector('[data-show]');
      
      const mobPriceElement = this.querySelector('[data-mob-price]');
      const mobCompareAtPriceElement = this.querySelector('[data-mob-compare-at-price]');
      const mobPriceContainer = this.querySelector('.recommended-product__price');
      const mobCompareAtPriceWrapper = mobCompareAtPriceElement ? mobCompareAtPriceElement.parentElement : null;

      if (!qtyInput || !qtyMinus || !qtyPlus || !priceContainer || !priceElement) return;

      const variantPrice = parseInt(priceContainer.dataset.variantPrice) || 0;
      const variantCompareAtPrice = parseInt(priceContainer.dataset.variantCompareAtPrice) || 0;
      const isoCode = window.KROWN?.settings?.iso_code ? ' ' + window.KROWN.settings.iso_code : '';

      const formatMoney = (cents) => {
        if (!window.KROWN?.settings?.shop_money_format) {
          return (cents / 100).toFixed(2) + isoCode;
        }

        const format = window.KROWN.settings.shop_money_format;
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        
        function formatWithDelimiters(number, precision, thousands, decimal) {
          thousands = thousands || ',';
          decimal = decimal || '.';
          
          if (isNaN(number) || number === null) {
            return 0;
          }
          
          number = (number / 100.0).toFixed(precision);
          
          const parts = number.split('.');
          const dollarsAmount = parts[0].replace(
            /(\d)(?=(\d\d\d)+(?!\d))/g,
            '$1' + thousands
          );
          const centsAmount = parts[1] ? decimal + parts[1] : '';
          
          return dollarsAmount + centsAmount;
        }

        let value = '';
        const match = format.match(placeholderRegex);
        
        if (match) {
          switch (match[1]) {
            case 'amount':
              value = formatWithDelimiters(cents, 2);
              break;
            case 'amount_no_decimals':
              value = formatWithDelimiters(cents, 0);
              break;
            case 'amount_with_comma_separator':
              value = formatWithDelimiters(cents, 2, '.', ',');
              break;
            case 'amount_no_decimals_with_comma_separator':
              value = formatWithDelimiters(cents, 0, '.', ',');
              break;
            case 'amount_no_decimals_with_space_separator':
              value = formatWithDelimiters(cents, 0, ' ');
              break;
            case 'amount_with_apostrophe_separator':
              value = formatWithDelimiters(cents, 2, "'");
              break;
            default:
              value = formatWithDelimiters(cents, 2);
          }
        } else {
          value = formatWithDelimiters(cents, 2);
        }

        return format.replace(placeholderRegex, value) + isoCode;
      };

      const updatePrice = () => {
        const quantity = parseInt(qtyInput.value) || 1;
        const totalPrice = variantPrice * quantity;
        const totalCompareAtPrice = variantCompareAtPrice * quantity;
        const formattedPrice = formatMoney(totalPrice);
        const formattedCompareAtPrice = formatMoney(totalCompareAtPrice);

        priceElement.textContent = formattedPrice;

        if (compareAtPriceElement && compareAtPriceWrapper) {
          if (variantCompareAtPrice > variantPrice && totalCompareAtPrice > totalPrice) {
            compareAtPriceElement.textContent = formattedCompareAtPrice;
            compareAtPriceWrapper.style.display = '';
          } else {
            compareAtPriceWrapper.style.display = 'none';
          }
        }

        // Update mobile price only on screens <= 768px
        if (window.innerWidth <= 768) {
          if (mobPriceElement) {
            mobPriceElement.textContent = formattedPrice;
          }

          if (mobCompareAtPriceElement && mobCompareAtPriceWrapper) {
            if (variantCompareAtPrice > variantPrice && totalCompareAtPrice > totalPrice) {
              mobCompareAtPriceElement.textContent = formattedCompareAtPrice;
              if (mobCompareAtPriceWrapper) {
                mobCompareAtPriceWrapper.style.display = '';
              }
            } else {
              if (mobCompareAtPriceWrapper) {
                mobCompareAtPriceWrapper.style.display = 'none';
              }
            }
          }
        }
      };

      qtyMinus.addEventListener('click', (e) => {
        e.preventDefault();
        const currentQty = parseInt(qtyInput.value) || 1;
        const minQty = parseInt(qtyInput.getAttribute('min')) || 1;
        if (currentQty > minQty) {
          qtyInput.value = currentQty - 1;
          updatePrice();
        }
      });

      qtyPlus.addEventListener('click', (e) => {
        e.preventDefault();
        const currentQty = parseInt(qtyInput.value) || 1;
        qtyInput.value = currentQty + 1;
        updatePrice();
      });

      qtyInput.addEventListener('input', () => {
        const value = parseInt(qtyInput.value) || 1;
        const minQty = parseInt(qtyInput.getAttribute('min')) || 1;
        if (value < minQty) {
          qtyInput.value = minQty;
        }
        updatePrice();
      });

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitButton = form.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.classList.add('working');
          submitButton.disabled = true;
        }

        const formData = new FormData(form);
        const body = new URLSearchParams();
        
        for (const [key, value] of formData.entries()) {
          body.append(key, value);
        }

        try {
          const response = await fetch(`${window.KROWN?.settings?.routes?.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: body.toString()
          });

          const data = await response.json();

          if (response.status === 422) {
            const alert = document.createElement('span');
            alert.className = 'alert alert--error';
            alert.textContent = data.description || data.message || 'Error adding product to cart';
            form.prepend(alert);
            
            if (submitButton) {
              submitButton.classList.remove('working');
              submitButton.disabled = false;
            }
            return;
          }

          // Reload page after adding to cart from main cart page
          setTimeout(() => {
            window.location.reload();
          }, 500);

        } catch (error) {
          console.error('Error adding product to cart:', error);
          const alert = document.createElement('span');
          alert.className = 'alert alert--error';
          alert.textContent = window.KROWN?.settings?.locales?.cart_general_error || 'Error adding product to cart';
          form.prepend(alert);
          
          if (submitButton) {
            submitButton.classList.remove('working');
            submitButton.disabled = false;
          }
        }
      });

      updatePrice();
    }

    initDrawerButton(button) {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const variantId = button.getAttribute('data-variant-id');
        if (!variantId) {
          console.error('No variant ID found');
          return;
        }

        // Disable button and add loading state
        button.classList.add('working');
        button.disabled = true;

        const body = new URLSearchParams();
        body.append('id', variantId);
        body.append('quantity', '1');

        try {
          const response = await fetch(`${window.KROWN?.settings?.routes?.cart_add_url || '/cart/add'}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: body.toString()
          });

          const data = await response.json();

          if (response.status === 422) {
            const alert = document.createElement('span');
            alert.className = 'alert alert--error';
            alert.textContent = data.description || data.message || 'Error adding product to cart';
            
            // Insert error message before the button
            const actionsForm = button.closest('.recommended-product__actions-form');
            if (actionsForm) {
              actionsForm.insertBefore(alert, button);
            }
            
            button.classList.remove('working');
            button.disabled = false;
            return;
          }

          // Success - refresh cart
          if (window.refreshCart) {
            window.refreshCart();
            
            setTimeout(() => {
              button.classList.remove('working');
              button.disabled = false;
              
              // Scroll to top of sidebar
              const sidebar = document.getElementById('site-cart-sidebar');
              if (sidebar) {
                const container = sidebar.querySelector('.sidebar__container');
                if (container) {
                  container.scrollTo({top: 0, behavior: 'smooth'});
                }
              }
            }, 500);
          } else {
            button.classList.remove('working');
            button.disabled = false;
          }

        } catch (error) {
          console.error('Error adding product to cart:', error);
          const alert = document.createElement('span');
          alert.className = 'alert alert--error';
          alert.textContent = window.KROWN?.settings?.locales?.cart_general_error || 'Error adding product to cart';
          
          const actionsForm = button.closest('.recommended-product__actions-form');
          if (actionsForm) {
            actionsForm.insertBefore(alert, button);
          }
          
          button.classList.remove('working');
          button.disabled = false;
        }
      });
    }

  }

  if ( typeof customElements.get('recommended-product') == 'undefined' ) {
    customElements.define('recommended-product', RecommendedProduct);
  }

  document.addEventListener('cart-updated', () => {
    setTimeout(() => {
      document.querySelectorAll('recommended-product').forEach(element => {
        if (element && !element.hasAttribute('data-initialized')) {
          element.setAttribute('data-initialized', 'true');
          const instance = new RecommendedProduct();
          Object.setPrototypeOf(instance, RecommendedProduct.prototype);
          instance.init.call(element);
        }
      });
    }, 100);
  });

}
